import React, { useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useCourseEvaluationApi } from '../../../hooks/useCourseEvaluationApi';
import { showAlert } from '../../../utils/sweetAlertUtils';

const CourseDuplicateExamineeExport: React.FC = () => {
    const { extractDuplicates, isLoading } = useCourseEvaluationApi();

    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    const handleClear = () => {
        setFromDate('');
        setToDate('');
    };

    const breadcrumbs = [
        { label: 'Admin', href: '#' },
        { label: 'Reports', href: '#' },
        { label: 'Courses', href: '#' },
        { label: 'Duplicate Examinee ID', href: '#' },
    ];

    const handleExport = async () => {
        // Validate that both dates are required
        if (!fromDate || !toDate) {
            showAlert({
                title: 'Validation Error',
                text: 'Please select both From Date and To Date',
                icon: 'error',
            });
            return;
        }

        // Validate that fromDate is not after toDate
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            showAlert({
                title: 'Validation Error',
                text: 'From Date cannot be after To Date',
                icon: 'error',
            });
            return;
        }

        try {
            const response = await extractDuplicates(
                fromDate,
                toDate
            );

            if (response.data) {
                // Open the SAS URL in a new window to download the Excel file
                window.open(response.data, '_blank');
                showAlert({
                    title: 'Export Successful',
                    text: 'Your duplicate examinee ID report has been generated and will download shortly.',
                    icon: 'success',
                });
            }
        } catch (error) {
            console.error('Error exporting duplicate examinee IDs:', error);
            showAlert({
                title: 'Export Failed',
                text: 'An error occurred while generating the export. Please try again.',
                icon: 'error',
            });
        }
    };

    return (
        <AdminLayout
            activeMenuItem="/admin/reports"
            breadcrumbs={breadcrumbs}
            allowedRoles={['Admin']}
            requiredPermission="Reports.Courses.View"
        >
            <Card
                title="Duplicate Examinee ID Export"
                subtitle="Export course evaluations where the same examinee ID has been used by different members within the specified date range."
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                        id="fromDate"
                        label="From Date"
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        placeholder="Select From Date"
                        allowFutureDates={false}
                        required={true}
                    />
                    <FormField
                        id="toDate"
                        label="To Date"
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        placeholder="Select To Date"
                        allowFutureDates={false}
                        required={true}
                    />
                </div>
                <div className="mt-6 flex gap-3">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClear}
                        disabled={isLoading}
                    >
                        Clear
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleExport}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Generating...' : 'Export'}
                    </Button>
                </div>
            </Card>
        </AdminLayout>
    );
};

export default CourseDuplicateExamineeExport;
